import { useForm, ValidationError } from '@formspree/react'
import { Phone, Mail, MapPin, CheckCircle, Loader2, FileText, Download } from 'lucide-react'
import { useState } from 'react'
import Reveal from './Reveal'

const GUIDE_POINTS = [
  'Local SEO basics for Melbourne suburbs',
  'A mobile-first design checklist',
  'Trust signals that actually convert',
  'The common mistakes that cost you leads',
]

const LeadMagnetForm = () => {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const response = await fetch('https://formspree.io/f/xreljzay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          subject: 'Lead Magnet Download Request',
          message: `Lead magnet download requested by: ${email}`,
        }),
      })
      if (response.ok) {
        setSubmitted(true)
        setTimeout(() => window.open('/5-things-website-must-have.html', '_blank'), 1200)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-3 rounded-2xl bg-gold/10 p-4 text-sm text-ink">
        <CheckCircle className="h-5 w-5 flex-shrink-0 text-gold" />
        Opening your guide in a new tab now.
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder="Your email address"
        className="min-w-0 flex-1 rounded-full border border-cream/25 bg-cream/10 px-5 py-3 text-cream outline-none transition placeholder:text-cream/45 focus:border-gold-bright"
      />
      <button type="submit" disabled={submitting} className="btn btn-gold whitespace-nowrap disabled:opacity-70">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <><Download className="h-4 w-4" /> Get the guide</>}
      </button>
    </form>
  )
}

const inputClass =
  'w-full rounded-xl border border-[var(--line-strong)] bg-paper px-4 py-3 text-ink outline-none transition placeholder:text-ink-faint focus:border-gold focus:ring-2 focus:ring-gold/20'

const Contact = () => {
  const [state, handleSubmit] = useForm('xreljzay')

  return (
    <section id="contact" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-container px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Tell me what your business needs.
          </h2>
          <p className="mt-6 text-lg text-ink-soft">
            Website, Google Business Profile help, or maintenance? Send a few details and I will reply
            with the next step, usually within 24 hours.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          {/* Left — info + lead magnet */}
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-xl2 border border-[var(--line)] bg-paper p-7 shadow-soft">
                <h3 className="font-display text-xl font-bold text-ink">Reach me directly</h3>
                <div className="mt-5 space-y-4">
                  <a href="mailto:hello@crateweb.com.au" className="group flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold"><Mail className="h-4 w-4" /></span>
                    <span className="text-ink-soft transition-colors group-hover:text-ink">hello@crateweb.com.au</span>
                  </a>
                  <a href="tel:0450692980" className="group flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold"><Phone className="h-4 w-4" /></span>
                    <span className="text-ink-soft transition-colors group-hover:text-ink">0450 692 980</span>
                  </a>
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold"><MapPin className="h-4 w-4" /></span>
                    <span className="text-ink-soft">Melbourne, Australia</span>
                  </div>
                </div>
              </div>

              {/* Lead magnet */}
              <div className="rounded-xl2 bg-ink p-7 text-cream shadow-card">
                <div className="inline-flex items-center gap-2 rounded-full bg-cream/10 px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-wider text-gold-bright">
                  <FileText className="h-3.5 w-3.5" /> Free guide
                </div>
                <h3 className="font-display mt-4 text-xl font-bold">5 things your business website must have</h3>
                <p className="mt-2 text-sm text-cream/70">A short, practical guide for Melbourne business owners.</p>
                <ul className="mt-4 space-y-2">
                  {GUIDE_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[0.85rem] text-cream/85">
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-bright" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-5"><LeadMagnetForm /></div>
                <p className="mt-3 text-center text-[0.75rem] text-cream/45">Instant access. No spam.</p>
              </div>
            </div>
          </Reveal>

          {/* Right — form */}
          <Reveal delay={0.08} className="lg:col-span-3">
            {state.succeeded ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl2 border border-[var(--line)] bg-paper p-12 text-center shadow-soft">
                <span className="grid h-16 w-16 place-items-center rounded-full bg-gold/10 text-gold">
                  <CheckCircle className="h-8 w-8" />
                </span>
                <h3 className="font-display mt-5 text-2xl font-bold text-ink">Message sent</h3>
                <p className="mt-2 text-ink-soft">Thanks for reaching out. I will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-xl2 border border-[var(--line)] bg-paper p-7 shadow-card lg:p-9">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-semibold text-ink">Your name *</label>
                    <input id="name" name="name" type="text" required placeholder="Jane Doe" className={inputClass} />
                    <ValidationError prefix="Name" field="name" errors={state.errors} className="mt-1 text-sm text-red-600" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-semibold text-ink">Email address *</label>
                    <input id="email" name="email" type="email" required placeholder="jane@business.com.au" className={inputClass} />
                    <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-1 text-sm text-red-600" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-ink">Phone number</label>
                    <input id="phone" name="phone" type="tel" placeholder="04XX XXX XXX" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="business" className="mb-2 block text-sm font-semibold text-ink">Business name</label>
                    <input id="business" name="business" type="text" placeholder="Smith & Co" className={inputClass} />
                  </div>
                </div>

                <div className="mt-5">
                  <span className="mb-3 block text-sm font-semibold text-ink">What are you interested in?</span>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { id: 'web-dev', label: 'Website design' },
                      { id: 'gbp', label: 'Google Business Profile' },
                      { id: 'maintenance', label: 'Website maintenance' },
                      { id: 'local-seo', label: 'Local SEO support' },
                    ].map((s) => (
                      <label
                        key={s.id}
                        className="flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--line)] px-4 py-3 transition-colors hover:border-gold hover:bg-gold/5"
                      >
                        <input type="checkbox" name="services" value={s.label} className="h-4 w-4 rounded border-ink-faint text-gold focus:ring-gold" />
                        <span className="text-sm text-ink-soft">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="message" className="mb-2 block text-sm font-semibold text-ink">Message</label>
                  <textarea id="message" name="message" rows={4} placeholder="Tell me a bit about your project..." className={`${inputClass} resize-none`} />
                </div>

                <button type="submit" disabled={state.submitting} className="btn btn-primary mt-6 w-full disabled:opacity-70">
                  {state.submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : 'Get my fixed-price quote'}
                </button>
                <p className="mt-4 text-center text-sm text-ink-faint">
                  Prefer email? <a href="mailto:hello@crateweb.com.au" className="font-medium text-gold">hello@crateweb.com.au</a>
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export default Contact
